import { useState } from 'react'
import {
  useAssignLocale,
  useCreateLocale,
  useLocales,
  usePatchLocale,
  usePatchLocalePluralForms,
  usePlatforms,
  useUnassignLocale,
  useUntranslatedCount,
} from '../api/hooks'
import type { LocaleInfo, TranslateChoice } from '../api/types'
import { Modal } from '../components/Modal'
import { localeLabel } from '../lib/locales'

// CLDR order, matching api/cldr_plurals.py's ALL_CATEGORIES.
const ALL_CLDR_FORMS = ['zero', 'one', 'two', 'few', 'many', 'other'] as const

export function LocalesTab() {
  const locales = useLocales()
  const platforms = usePlatforms()
  const patch = usePatchLocale()
  const [showAdd, setShowAdd] = useState(false)
  const [assignTarget, setAssignTarget] = useState<{ platform: string; locale: LocaleInfo } | null>(
    null,
  )

  const platformSlugs = (platforms.data ?? []).map((p) => p.slug)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-600">
          German ships as two separate locales, never a single "German" — <strong>de-sie</strong> is
          formal (Sie), <strong>de-du</strong> is informal (Du). A new locale starts assigned to
          zero platforms: turning it on for a platform is the moment translation work (and AI
          cost) begins, and you always choose AI or manual explicitly.
        </p>
        <button className="btn btn-primary shrink-0" onClick={() => setShowAdd(true)}>
          + Add a language
        </button>
      </div>

      {locales.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load locales: {(locales.error as Error).message}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Code</th>
              <th className="px-3 py-2 text-left font-semibold">Language</th>
              <th className="px-3 py-2 text-left font-semibold">Formality</th>
              <th className="px-3 py-2 text-left font-semibold">Plural forms</th>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Actions</th>
              <th className="px-3 py-2 text-left font-semibold">Platforms</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(locales.data ?? []).map((locale) => (
              <LocaleRow
                key={locale.code}
                locale={locale}
                platformSlugs={platformSlugs}
                busy={patch.isPending}
                onToggle={() => onToggleGlobal(locale, patch.mutate)}
                onRequestAssign={(platform) => setAssignTarget({ platform, locale })}
              />
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddLocaleDialog onClose={() => setShowAdd(false)} />}
      {assignTarget && (
        <AssignLocaleDialog
          platform={assignTarget.platform}
          locale={assignTarget.locale}
          onClose={() => setAssignTarget(null)}
        />
      )}
    </div>
  )
}

function onToggleGlobal(
  locale: LocaleInfo,
  mutate: ReturnType<typeof usePatchLocale>['mutate'],
): void {
  const label = localeLabel(locale)
  if (locale.enabled) {
    if (!window.confirm(`Disable ${label.full}? Its column stops accepting new AI translations.`)) return
    mutate({ code: locale.code, enabled: false })
    return
  }
  if (
    !window.confirm(
      `Enable ${label.full}? This queues an AI translation backfill job for every existing key on every platform it is already assigned to.`,
    )
  )
    return
  mutate({ code: locale.code, enabled: true })
}

function LocaleRow({
  locale,
  platformSlugs,
  busy,
  onToggle,
  onRequestAssign,
}: {
  locale: LocaleInfo
  platformSlugs: string[]
  busy: boolean
  onToggle: () => void
  onRequestAssign: (platform: string) => void
}) {
  const label = localeLabel(locale)
  const unassign = useUnassignLocale()
  return (
    <tr>
      <td className="px-3 py-2 font-mono text-ink-800">{locale.code}</td>
      <td className="px-3 py-2">{label.full}</td>
      <td className="px-3 py-2">
        {label.variant ? (
          <span className="chip border-ink-300 bg-white text-ink-700">{label.variant}</span>
        ) : (
          <span className="text-ink-500">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <PluralFormsCell locale={locale} />
      </td>
      <td className="px-3 py-2">
        {locale.is_source ? (
          <span className="chip border-blue-300 bg-blue-50 text-blue-800">source</span>
        ) : (
          <span className="text-ink-500">target</span>
        )}
      </td>
      <td className="px-3 py-2">
        <span
          className={`chip border ${locale.enabled ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-ink-300 bg-ink-100 text-ink-600'}`}
        >
          {locale.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </td>
      <td className="px-3 py-2">
        <button
          className="btn"
          disabled={busy || locale.is_source}
          title={locale.is_source ? 'The source locale cannot be disabled' : undefined}
          onClick={onToggle}
        >
          {locale.enabled ? 'Disable' : 'Enable'}
        </button>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1.5">
          {platformSlugs.map((platform) => {
            const assigned = locale.is_source || locale.platforms.includes(platform)
            return (
              <button
                key={platform}
                className={`chip border ${assigned ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-ink-300 bg-white text-ink-600'}`}
                disabled={locale.is_source || unassign.isPending}
                title={
                  locale.is_source
                    ? 'The source locale is assigned to every platform by default'
                    : assigned
                      ? `Remove ${locale.code} from ${platform} (kept translations stay; queued AI jobs for it are cancelled)`
                      : `Assign ${locale.code} to ${platform}`
                }
                onClick={() => {
                  if (assigned) {
                    if (
                      !window.confirm(
                        `Remove ${locale.code} from ${platform}? Existing translations are kept, but any pending AI jobs for it on ${platform} are cancelled.`,
                      )
                    )
                      return
                    unassign.mutate({ platform, code: locale.code })
                  } else {
                    onRequestAssign(platform)
                  }
                }}
              >
                {platform}
              </button>
            )
          })}
        </div>
      </td>
    </tr>
  )
}

/** Shows a locale's required CLDR forms and, on demand, lets an admin
 *  correct them -- an unverified one/other guess (the language subtag has no
 *  entry in cldr_plurals.py) is visibly distinct from a checked CLDR set, so
 *  nobody mistakes a guess for something that was actually verified. */
function PluralFormsCell({ locale }: { locale: LocaleInfo }) {
  const patchForms = usePatchLocalePluralForms()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<string[]>(locale.plural_forms)

  if (editing) {
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-2">
          {ALL_CLDR_FORMS.map((form) => (
            <label key={form} className="flex items-center gap-1 font-mono text-2xs text-ink-700">
              <input
                type="checkbox"
                checked={draft.includes(form)}
                onChange={(event) =>
                  setDraft((current) =>
                    event.target.checked
                      ? [...current, form]
                      : current.filter((existing) => existing !== form),
                  )
                }
              />
              {form}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            className="btn btn-primary"
            disabled={draft.length === 0 || patchForms.isPending}
            onClick={() =>
              patchForms.mutate(
                { code: locale.code, pluralForms: draft },
                { onSuccess: () => setEditing(false) },
              )
            }
          >
            {patchForms.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {locale.plural_forms.map((form) => (
        <span key={form} className="chip border-ink-300 bg-white font-mono text-ink-700">
          {form}
        </span>
      ))}
      {locale.plural_forms_verified ? (
        <span
          className="chip border-emerald-300 bg-emerald-50 text-emerald-800"
          title="Matches a checked CLDR rule, or an admin has confirmed it."
        >
          CLDR verified
        </span>
      ) : (
        <span
          className="chip border-amber-300 bg-amber-50 text-amber-900"
          title="No verified CLDR rule for this language -- this is an unchecked one/other guess. Please confirm or correct it."
        >
          ⚠ unverified guess
        </span>
      )}
      <button
        className="btn"
        onClick={() => {
          setDraft(locale.plural_forms)
          setEditing(true)
        }}
      >
        Edit
      </button>
    </div>
  )
}

function AddLocaleDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateLocale()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [formality, setFormality] = useState<'none' | 'formal' | 'informal'>('none')

  const submit = () => {
    create.mutate(
      { code: code.trim(), name: name.trim(), formality: formality === 'none' ? null : formality },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      title="Add a language"
      description="Free-form locale code — not a predefined list. It starts assigned to zero platforms; nothing is translated until you assign it to one."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!code.trim() || !name.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? 'Adding…' : 'Add locale'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="new-locale-code">
            Code
          </label>
          <input
            id="new-locale-code"
            className="field mt-1 font-mono"
            placeholder="tr, fr-CA, ar…"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
          <p className="mt-1 text-2xs text-ink-600">
            Letters, digits and hyphens only (e.g. BCP-47 style). Case is normalised
            automatically.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="new-locale-name">
            Display name
          </label>
          <input
            id="new-locale-name"
            className="field mt-1"
            placeholder="Turkish"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-locale-formality">
            Formality
          </label>
          <select
            id="new-locale-formality"
            className="field mt-1"
            value={formality}
            onChange={(event) => setFormality(event.target.value as typeof formality)}
          >
            <option value="none">None</option>
            <option value="formal">Formal (e.g. Sie / vous / usted)</option>
            <option value="informal">Informal (e.g. du / tu / tú)</option>
          </select>
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

function AssignLocaleDialog({
  platform,
  locale,
  onClose,
}: {
  platform: string
  locale: LocaleInfo
  onClose: () => void
}) {
  const assign = useAssignLocale()
  const preview = useUntranslatedCount(platform, locale.code)
  const [choice, setChoice] = useState<TranslateChoice | null>(null)
  const label = localeLabel(locale)

  const submit = (translate: TranslateChoice) => {
    setChoice(translate)
    assign.mutate({ platform, code: locale.code, translate }, { onSuccess: onClose })
  }

  return (
    <Modal
      title={`Assign ${label.full} to ${platform}`}
      description="This is the moment translation work — and AI cost — begins. Choose explicitly; there is no default."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={assign.isPending}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={assign.isPending}
            onClick={() => submit('manual')}
          >
            {assign.isPending && choice === 'manual' ? 'Assigning…' : "I'll translate manually"}
          </button>
          <button
            className="btn btn-primary"
            disabled={assign.isPending}
            onClick={() => submit('ai')}
          >
            {assign.isPending && choice === 'ai' ? 'Queuing…' : 'Translate with AI now'}
          </button>
        </>
      }
    >
      <div className="space-y-2 text-xs text-ink-700">
        {preview.isLoading && <p>Checking how many keys need {label.full}…</p>}
        {preview.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-rose-900">
            Could not load the untranslated count: {(preview.error as Error).message}
          </p>
        )}
        {preview.data && (
          <p>
            <strong>{preview.data.untranslated}</strong> key
            {preview.data.untranslated === 1 ? '' : 's'} on <strong>{platform}</strong> currently
            have no {label.full} translation.
          </p>
        )}
        <p>
          <strong>Translate with AI now</strong> queues a backfill job for every one of those keys.{' '}
          <strong>I'll translate manually</strong> assigns the locale and queues nothing — it goes
          live immediately serving the source-language fallback, and you can bulk-translate later.
        </p>
        {assign.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-rose-900">
            {(assign.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}
